// src/App.js

import { useState, useEffect, useMemo, useContext } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Importez les deux listes de routes
import { privateRoutes, publicRoutes } from "./routes";

// Importez le composant PrivateRoute
import PrivateRoute from "./components/PrivateRoute";

import { useMaterialUIController } from "context";
import AuthContext from "./context/AuthContext";
import Unauthorized from "layouts/unauthorized";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

export default function App() {
  const [controller] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const showDashboardLayout = !pathname.includes("/authentication/sign-in");

  const { user } = useContext(AuthContext);
  const userRole = user?.profile?.role || user?.role;
  const userAllRoles = useMemo(
    () => user?.profile?.all_roles || (userRole ? [userRole] : []),
    [user, userRole]
  );

  const filteredRoutes = useMemo(() => {
    return privateRoutes.filter((route) => {
      if (route.roles && !route.roles.some((r) => userAllRoles.includes(r))) return false;
      if (route.excludeRoles && route.excludeRoles.some((r) => userAllRoles.includes(r)))
        return false;
      return true;
    });
  }, [userAllRoles]);

  const allPrivateRoutesWithGuard = useMemo(() => {
    return privateRoutes.map((route) => {
      const isAllowed = filteredRoutes.some((r) => r.key === route.key);
      if (isAllowed) return route;
      return { ...route, component: <Navigate to="/unauthorized" replace /> };
    });
  }, [filteredRoutes]);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      // ... (votre logique existante)
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      // ... (votre logique existante)
    }
  };

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && showDashboardLayout && (
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            //brandName={process.env.REACT_APP_BRAND_NAME}
            routes={filteredRoutes}
          />
        )}
        <Routes>
          {publicRoutes.map((route, key) => (
            <Route path={route.route} element={route.component} key={key} />
          ))}
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route element={<PrivateRoute />}>
            {allPrivateRoutesWithGuard.map((route, key) => (
              <Route path={route.route} element={route.component} key={key} />
            ))}
          </Route>
          <Route path="*" element={<Navigate to="/home" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && showDashboardLayout && (
        <Sidenav
          color={sidenavColor}
          brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
          //brandName={process.env.REACT_APP_BRAND_NAME}
          routes={filteredRoutes}
        />
      )}
      <Routes>
        {publicRoutes.map((route, key) => (
          <Route path={route.route} element={route.component} key={key} />
        ))}
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route element={<PrivateRoute />}>
          {allPrivateRoutesWithGuard.map((route, key) => (
            <Route path={route.route} element={route.component} key={key} />
          ))}
        </Route>
        <Route path="*" element={<Navigate to="/home" />} />
      </Routes>
    </ThemeProvider>
  );
}
